import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import RootStoreContext from '~/contexts/RootStoreContext';
import { actions as modalActions } from '~/domains/modal';
import { selectors as agentScannersSelectors } from '~/domains/agentScanners';
import {
  actions as commonActions,
  selectors as commonSelectors
} from '~/domains/common';
import DeleteConfirmModal from '../ScanConfigurationActions/DeleteConfirmModal';
import ScanConfigurationsListComponent from './ScanConfigurationsList';

/**
 * Container for ScanConfigurationsList component
 * Connects to Redux store following the same pattern as other containers
 */

const mapStateToProps = (state, ownProps) => {
  const profile = commonSelectors.profile(state);
  const organization = profile?.organization;
  const organizationId = organization?.id;

  return {
    // Use filtered data from props if available, otherwise use Redux data
    scanConfigurations:
      ownProps.scanConfigurations || agentScannersSelectors.scanners(state),
    isFetchingScanConfigurations:
      agentScannersSelectors.isFetchingScanners(state),
    scannersError: agentScannersSelectors.scannersError(state),

    // Organization context
    organizationId,
    alert: commonSelectors.alert(state)
  };
};

const mapDispatchToProps = (dispatch, ownProps) => {
  // Import actions dynamically to avoid circular dependencies
  // eslint-disable-next-line global-require
  const { actions: agentScannersActions } = require('~/domains/agentScanners');

  return {
    // Action creators
    onCloseAlert: () => dispatch(commonActions.closeAlert()),
    openModal: ({ modalType, modalProps }) =>
      dispatch(modalActions.openModal({ modalType, modalProps })),
    onLoadScanConfigurations: (organizationId) =>
      dispatch(agentScannersActions.loadAgentScanners(organizationId)),

    // Use the handler from parent props, or fallback to no-op
    onScanConfigurationClick:
      ownProps.onScanConfigurationClick ||
      (() => {
        // Fallback handler - no action needed
      }),

    onRunNowClick: (organizationId, scanConfig) => {
      const startTime = Date.now();
      const MIN_DISPLAY_TIME = 3000; // 3 seconds minimum

      // Trigger the scan execution via the agentScanners action
      dispatch(
        agentScannersActions.runScanConfiguration(organizationId, scanConfig.id)
      )
        .then(() => {
          // Ensure alert is visible for at least MIN_DISPLAY_TIME
          const elapsed = Date.now() - startTime;
          const remainingTime = Math.max(0, MIN_DISPLAY_TIME - elapsed);

          setTimeout(() => {
            dispatch(commonActions.clearAlert());
            // Reload the list after alert closes to give backend time to update
            if (organizationId) {
              dispatch(agentScannersActions.loadAgentScanners(organizationId));
            }
          }, remainingTime);
        })
        .catch(() => {
          // Ensure alert is visible for at least MIN_DISPLAY_TIME even on error
          const elapsed = Date.now() - startTime;
          const remainingTime = Math.max(0, MIN_DISPLAY_TIME - elapsed);

          setTimeout(() => {
            dispatch(commonActions.clearAlert());
          }, remainingTime);
        });

      // Show info alert while running
      // TODO: Implement Abort action
      const runAlert = {
        type: 'info',
        message: `Your '${scanConfig.name}' is currently updating.<br>Click <a onclick="() => null">Abort</a> if you need to stop it now.`,
        isHTML: true
      };

      dispatch(commonActions.setAlert(runAlert));
    },

    onEditScannerClick:
      ownProps.onEditScannerClick ||
      (() => {
        // Fallback handler - no action needed
      }),

    onDeleteScannerClick: (scanConfigId) => {
      // TODO: Delete scanner action
      dispatch(
        modalActions.openModal({
          modalType: DeleteConfirmModal.modalType,
          modalProps: {
            scanConfigId
          }
        })
      );
    },

    onAddScanner:
      ownProps.onAddScanner ||
      (() => {
        // Fallback handler - no action needed
      })
  };
};

const mergeProps = (stateProps, dispatchProps, ownProps) => ({
  ...ownProps,
  ...stateProps,
  ...dispatchProps,
  onRunNowClick: (scanConfig) =>
    dispatchProps.onRunNowClick(stateProps.organizationId, scanConfig)
});

export default compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps, mergeProps, {
    context: RootStoreContext
  })
)(ScanConfigurationsListComponent);
